import { validatedDisplayView } from "../../i18n/display-view.js";
import { portalText } from "../../i18n/portal.js";
/**
 * Area `audit-entry` of the console gateway — owned by plan-002 unit **RB-29** (design §15.6.9).
 *
 * The customer-visible audit log (P14-06's screen), over the two routes RB-29 added:
 * `GET /v1/audit-entries` (`listAuditEntries`) and `POST /v1/audit-entries/exports`
 * (`exportAuditEntries`, design §15.5.5's synchronous export). The register is EMPTY: both
 * operations are served.
 *
 * ── THE MAPPING ──────────────────────────────────────────────────────────────────────────
 * Every `…Text` field is worded here from facts (design §15.5.2): `whenText` from `occurred_at`
 * through `formatWhen`; `fromText` from `origin`; `expiresAtText` from `expires_at` against the
 * injected clock. `event_class` is the API's — derived there from the code's prefix by one table
 * — and an unknown value raises rather than landing under a guessed heading.
 *
 * ── AN OPERATOR IS NEVER SHOWN AS THE MEMBER (AC-33) ─────────────────────────────────────
 * `origin: "wetalk_support"` reads as "WeTalk support". A row with an `impersonation_grant_id`
 * always carries `impersonatedByOperatorName` — the name the entry recorded, or, if an older
 * writer left it out, a sentence saying so — so the screen can mark it whatever the name. An
 * actor the entry cannot name is worded as exactly that, never as somebody else.
 *
 * ── THE ORGANIZATION LOG AND THE FILTERS (plan-014 C4b, design §27.9.2) ───────────────────
 * `query.log` absent or `workspace` reads RB-29's `/v1/audit-entries`; `organization` reads
 * `/v1/accounts/{account_id}/audit-entries` (`listAccountAuditEntries`), across every workspace —
 * the API decides whether the reader sees every entry or Finance's financial ones only, and says
 * which (`finance_only`). The filters (`actor`, `result`, `from`, `to`) travel on both. A row now
 * carries the real and effective actor, the result, the target scope, the reason and the safe
 * before/after values; the before/after are worded here as field → value lines, never parsed for
 * anything else.
 */
import { assertOk, type WeTalkApiClient } from "@wetalk/api-client";

import type {
  AuditActorKindView,
  AuditChangeView,
  AuditOriginView,
  AuditEntryPageView,
  AuditEntryRowView,
  AuditEventClassView,
  AuditQueryView,
  AuditResultView,
  ConsoleGateway,
  ExportArtefactView,
} from "../gateway.js";
import {
  MalformedResponseError,
  readArray,
  readRecord,
  readText,
  readWholeNumber,
} from "../decode.js";
import type { GatewayGap, SliceContext } from "./slice-context.js";

declare module "../gateway.js" {
  interface ConsoleGateway {
    listAuditEntries(query: AuditQueryView): Promise<AuditEntryPageView>;
    exportAuditEntriesCsv(query: AuditQueryView): Promise<ExportArtefactView>;
  }
}

export const auditEntryGap: readonly GatewayGap[] = [];

type OriginView = AuditOriginView;

const EVENT_CLASS: readonly AuditEventClassView[] = ["voice_agent", "outbound", "billing", "compliance", "person"];
const RESULT: readonly AuditResultView[] = ["succeeded", "refused", "failed"];
type ScopeKind = "account" | "workspace" | "billing_account";
const SCOPE_KIND: readonly ScopeKind[] = ["account", "workspace", "billing_account"];

/** What the action was about, in the words the console uses. */
const SCOPE_COPY = () => ({
  account: portalText("shell", "organization_519255ae"),
  workspace: portalText("shell", "workspace_4ca0a75c"),
  billing_account: portalText("shell", "balance_90eef613"),
});

/** An effective actor the entry cannot name (a member since removed). Never borrowed from somebody else. */
const UNNAMED_EFFECTIVE = () => (portalText("shell", "a_member_no_longer_in_this_account_c6d8359d"));
const ACTOR_KIND: readonly AuditActorKindView[] = ["person", "operator", "system"];
const ORIGIN: readonly OriginView[] = ["console", "api_key", "wetalk_support", "system"];

/** "From" on the row. The API records no address, so a member's row says where, not which IP. */
const FROM_COPY: Readonly<Record<OriginView, string>> = validatedDisplayView({
  get console() { return portalText("shell", "console_9f3341d3"); },
  get api_key() { return portalText("shell", "api_key_cf678cab"); },
  get wetalk_support() { return portalText("shell", "wetalk_support_74a6daad"); },
  get system() { return portalText("shell", "automation_a15fde51"); },
});

/** How an actor the entry cannot name is worded. Never borrowed from somebody else. */
const UNNAMED_ACTOR_COPY: Readonly<Record<OriginView, string>> = validatedDisplayView({
  get console() { return portalText("shell", "a_member_no_longer_in_this_account_c6d8359d"); },
  get api_key() { return portalText("shell", "an_api_key_no_longer_held_ca9b02b4"); },
  get wetalk_support() { return portalText("shell", "a_wetalk_operator_name_not_recorded_7bee53d9"); },
  get system() { return portalText("shell", "wetalk_4b5a272f"); },
});

/** Icons by the action code's prefix where one reads better than its heading's. */
const PREFIX_ICON: Readonly<Record<string, string>> = {
  recording: "ph-duotone ph-headphones",
  conversation: "ph-duotone ph-headphones",
  impersonation: "ph-duotone ph-user-switch",
  campaign: "ph-duotone ph-phone-outgoing",
  api_key: "ph-duotone ph-key",
  membership: "ph-duotone ph-user-plus",
  invitation: "ph-duotone ph-user-plus",
  person_data_request: "ph-duotone ph-trash",
};

const EVENT_CLASS_ICON: Readonly<Record<AuditEventClassView, string>> = {
  voice_agent: "ph-duotone ph-git-branch",
  outbound: "ph-duotone ph-phone-outgoing",
  billing: "ph-duotone ph-credit-card",
  compliance: "ph-duotone ph-shield-check",
  person: "ph-duotone ph-users",
};

function readOneOf<T extends string>(
  value: unknown,
  path: string,
  allowed: readonly T[],
  wanted: string,
): T {
  const raw = readText(value, path);
  const found = allowed.find((one) => one === raw);
  if (found === undefined) throw new MalformedResponseError(path, wanted, raw);
  return found;
}

function readNullableText(value: unknown, path: string): string | null {
  return value === null ? null : readText(value, path);
}

/** A safe audit value, one line: text as itself, a number or a flag as written, anything nested as compact JSON. */
function valueText(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value === null) return "none";
  return JSON.stringify(value);
}

function valueRecordOf(value: unknown, path: string): Readonly<Record<string, unknown>> | null {
  return value === null || value === undefined ? null : readRecord(value, path);
}

/** The fields that differ between the safe before/after values, in the order they first appear. */
export function changeOf(before: Readonly<Record<string, unknown>> | null, after: Readonly<Record<string, unknown>> | null): readonly AuditChangeView[] {
  const field = [...new Set([...Object.keys(before ?? {}), ...Object.keys(after ?? {})])];
  const out: AuditChangeView[] = [];
  for (const one of field) {
    const was = before !== null && Object.hasOwn(before, one) ? valueText(before[one]) : null;
    const now = after !== null && Object.hasOwn(after, one) ? valueText(after[one]) : null;
    if (was === now) continue;
    out.push({ field: one.replace(/_/g, " "), beforeText: was, afterText: now });
  }
  return out;
}

function iconOf(actionCode: string, eventClass: AuditEventClassView): string {
  const dot = actionCode.indexOf(".");
  const prefix = dot === -1 ? actionCode : actionCode.slice(0, dot);
  // `account.impersonation_started` / `…_ended` are the operator console's spelling of the pair.
  if (actionCode.includes("impersonation")) return "ph-duotone ph-user-switch";
  return PREFIX_ICON[prefix] ?? EVENT_CLASS_ICON[eventClass];
}

/** One `AuditEntryRow` as the screen renders it. */
export function auditEntryRowOf(
  wire: unknown,
  path: string,
  now: Date,
  context: SliceContext,
): AuditEntryRowView {
  const row = readRecord(wire, path);
  const origin = readOneOf(row["origin"], `${path}.origin`, ORIGIN, portalText("shell", "an_audit_entry_origin_71f36ce3"));
  const eventClass = readOneOf(
    row["event_class"],
    `${path}.event_class`,
    EVENT_CLASS,
    portalText("shell", "one_of_the_four_audit_event_classes_979d017d"),
  );
  const actionCode = readText(row["action_code"], `${path}.action_code`);
  const actorName = readNullableText(row["actor_name"], `${path}.actor_name`);
  const grantId = readNullableText(row["impersonation_grant_id"], `${path}.impersonation_grant_id`);
  const operatorName = readNullableText(
    row["impersonated_by_operator_name"],
    `${path}.impersonated_by_operator_name`,
  );
  const realActor = readRecord(row["real_actor"], `${path}.real_actor`);
  const effective = row["effective_actor"] === null ? null : readRecord(row["effective_actor"], `${path}.effective_actor`);
  const targetScope = readRecord(row["target_scope"], `${path}.target_scope`);
  const scopeKind = readOneOf(targetScope["kind"], `${path}.target_scope.kind`, SCOPE_KIND, portalText("shell", "a_target_scope_45437314"));
  const effectiveName = effective === null ? null : readNullableText(effective["name"], `${path}.effective_actor.name`);
  return validatedDisplayView({
    auditEntryId: readText(row["audit_entry_id"], `${path}.audit_entry_id`),
    get whenText() { return context.formatWhen(readText(row["occurred_at"], `${path}.occurred_at`), now); },
    actorName: actorName ?? UNNAMED_ACTOR_COPY[origin],
    actorKind: readOneOf(row["actor_kind"], `${path}.actor_kind`, ACTOR_KIND, portalText("shell", "an_actor_kind_b4ca40a1")),
    origin,
    get summary() { return readText(row["summary"], `${path}.summary`); },
    actionCode,
    eventClass,
    fromText: FROM_COPY[origin],
    iconName: iconOf(actionCode, eventClass),
    impersonatedByOperatorName:
      grantId === null ? null : (operatorName ?? UNNAMED_ACTOR_COPY.wetalk_support),
    result: readOneOf(row["result"], `${path}.result`, RESULT, portalText("shell", "an_audit_result_4bf8e884")),
    realActorPersonId: readNullableText(realActor["person_id"], `${path}.real_actor.person_id`),
    effectiveActorName: effective === null ? null : (effectiveName ?? UNNAMED_EFFECTIVE()),
    effectiveActorPersonId: effective === null ? null : readText(effective["person_id"], `${path}.effective_actor.person_id`),
    get scopeText() { return SCOPE_COPY()[scopeKind]; },
    reason: readNullableText(row["reason"], `${path}.reason`),
    change: changeOf(valueRecordOf(row["before"], `${path}.before`), valueRecordOf(row["after"], `${path}.after`)),
    // plan-025: present only on a change confirmed on a wetalk help card.
    ...(row["help_action_id"] === undefined || row["help_action_id"] === null ? {} : { viaHelp: readText(row["help_action_id"], `${path}.help_action_id`) !== "" }),
  });
}

/** The filters as the wire names them (both logs). `event_class` and `cursor` are added by the caller. */
function filterQueryOf(query: AuditQueryView): Record<string, string> {
  const wire: Record<string, string> = {};
  const filter = query.filter;
  if (filter !== undefined) {
    if (filter.actorPersonId !== null) wire["actor"] = filter.actorPersonId;
    if (filter.result !== null) wire["result"] = filter.result;
    if (filter.from !== null) wire["from"] = filter.from;
    if (filter.to !== null) wire["to"] = filter.to;
  }
  return wire;
}

function pageOf(body: Readonly<Record<string, unknown>>, operationId: string, now: Date, context: SliceContext): AuditEntryPageView {
  const next = body["next_cursor"];
  const capped = body["matched_count_capped"];
  const financeOnly = body["finance_only"];
  if (typeof capped !== "boolean") throw new MalformedResponseError(`${operationId}.matched_count_capped`, "a boolean", String(capped));
  if (typeof financeOnly !== "boolean") throw new MalformedResponseError(`${operationId}.finance_only`, "a boolean", String(financeOnly));
  return {
    row: readArray(body["data"], `${operationId}.data`).map((one, index) => auditEntryRowOf(one, `auditEntry[${String(index)}]`, now, context)),
    nextCursor: next === null ? null : readText(next, `${operationId}.next_cursor`),
    matchedCount: readWholeNumber(body["matched_count"], `${operationId}.matched_count`),
    retentionMonth: readWholeNumber(body["retention_month"], `${operationId}.retention_month`),
    matchedCountCapped: capped,
    financeOnly,
  };
}

/** "in 15 minutes" — the link is short-lived, so the useful fact is how long it has left. */
export function expiresInText(expiresAt: string, now: Date): string {
  const minute = Math.ceil((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (Number.isNaN(minute)) throw new MalformedResponseError("expires_at", "a date-time", expiresAt);
  if (minute <= 0) return "expired";
  return minute === 1 ? portalText("shell", "in_1_minute_2298b716") : portalText("shell", "in_value_minutes_45992b3b", { v0: String(String(minute)) });
}

export function auditEntryApiSlice(
  client: WeTalkApiClient,
  context: SliceContext,
): Pick<ConsoleGateway, "listAuditEntries" | "exportAuditEntriesCsv"> {
  return {
    async listAuditEntries(query: AuditQueryView): Promise<AuditEntryPageView> {
      const now = context.now();
      const wireQuery: Record<string, string> = filterQueryOf(query);
      if (query.eventClass !== null) wireQuery["event_class"] = query.eventClass;
      if (query.cursor !== null) wireQuery["cursor"] = query.cursor;
      const log = query.log ?? { kind: "workspace" };
      if (log.kind === "organization") {
        const body = readRecord(
          assertOk(await client.listAccountAuditEntries({ path: { account_id: log.accountId }, query: wireQuery })),
          "listAccountAuditEntries",
        );
        return pageOf(body, "listAccountAuditEntries", now, context);
      }
      const body = readRecord(assertOk(await client.listAuditEntries({ query: wireQuery })), "listAuditEntries");
      return pageOf(body, "listAuditEntries", now, context);
    },

    async exportAuditEntriesCsv(query: AuditQueryView): Promise<ExportArtefactView> {
      const wireBody: Record<string, string | null> = { event_class: query.eventClass, ...filterQueryOf(query) };
      const log = query.log ?? { kind: "workspace" };
      const answered =
        log.kind === "organization"
          ? readRecord(
              assertOk(await client.exportAccountAuditEntries({ path: { account_id: log.accountId }, body: wireBody })),
              "exportAccountAuditEntries",
            )
          : readRecord(assertOk(await client.exportAuditEntries({ body: wireBody })), "exportAuditEntries");
      const data = readRecord(answered["data"], "exportAuditEntries.data");
      return validatedDisplayView({
        downloadUrl: readText(data["download_url"], "exportAuditEntries.download_url"),
        fileExtension: `.${readText(data["file_extension"], "exportAuditEntries.file_extension")}`,
        get expiresAtText() { return expiresInText(
          readText(data["expires_at"], "exportAuditEntries.expires_at"),
          context.now(),
        ); },
        rowCount: readWholeNumber(data["row_count"], "exportAuditEntries.row_count"),
      });
    },
  };
}
